// "Scan ID": read the barcode on the back of a driver license with the USB scanner or the camera.
import { useEffect, useRef, useState } from 'react';
import { ageOn, isExpired, parseLicense, type LicenseData } from '../../shared/license';
import { cameraReadsLicenses, scannerChar } from '../lib/license-scan';
import { Badge, Button, Sheet } from '../ui/kit';
import { Icon } from '../ui/icons';

const pad = (n: number) => String(n).padStart(2, '0');

/** Today in the store's time, as YYYY-MM-DD. */
export function todayIso(): string {
  const t = new Date();
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`;
}

/** 2026-08-31 → 08/31/2026 */
export function usDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

/** The back of a license, with its big barcode, so staff know which one to scan. */
function CardBack() {
  const rows = [0, 1, 2, 3, 4, 5];
  return (
    <svg className="card-back" viewBox="0 0 172 108" aria-hidden>
      <rect x="1" y="1" width="170" height="106" rx="10" fill="#fff" stroke="currentColor" strokeWidth="2" />
      <rect x="14" y="14" width="70" height="6" rx="3" fill="#D6DEEA" />
      <rect x="14" y="26" width="46" height="6" rx="3" fill="#D6DEEA" />
      {rows.map((r) => (
        <g key={r}>
          {Array.from({ length: 24 }, (_, i) => ((i * 7 + r * 5) % 3 === 0 ? null : <rect key={i} x={14 + i * 6} y={44 + r * 9} width={(i + r) % 4 === 0 ? 4 : 2.5} height="7" fill="#0B162D" />))}
        </g>
      ))}
    </svg>
  );
}

function CameraReader({ onText, onProblem }: { onText: (text: string) => void; onProblem: (msg: string) => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const text = useRef(onText);
  const problem = useRef(onProblem);
  text.current = onText;
  problem.current = onProblem;
  useEffect(() => {
    let stream: MediaStream | null = null;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    (async () => {
      try {
        const BD = (window as any).BarcodeDetector;
        const detector = new BD({ formats: ['pdf417'] });
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false });
        if (stopped) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        const v = video.current;
        if (!v) return;
        v.srcObject = stream;
        await v.play().catch(() => undefined);
        const tick = async () => {
          if (stopped) return;
          try {
            if (v.readyState >= 2) {
              const found: { rawValue?: string }[] = await detector.detect(v);
              const hit = found.find((b) => b.rawValue);
              if (hit?.rawValue) text.current(hit.rawValue);
            }
          } catch {
            // A frame that couldn't be read: try the next one.
          }
          if (!stopped) timer = setTimeout(tick, 180);
        };
        tick();
      } catch (e: any) {
        if (stopped) return;
        problem.current(e?.name === 'NotAllowedError' ? 'The camera is blocked for STORE OS. Allow it (the camera icon in the address bar), then try again.'
          : e?.name === 'NotFoundError' || e?.name === 'OverconstrainedError' ? 'This device has no camera STORE OS can use.'
          : 'The camera didn’t start. Close other apps using it and try again.');
      }
    })();
    return () => {
      stopped = true;
      clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);
  return (
    <div className="scan-video">
      <video ref={video} playsInline muted autoPlay aria-label="Camera" />
      <div className="scan-guide" aria-hidden />
    </div>
  );
}

/**
 * The scan window. While it's open, everything the USB scanner types is the license; or the
 * camera reads it. Gives the license to onScan and closes.
 */
export function ScanIdSheet({ onClose, onScan }: { onClose: () => void; onScan: (d: LicenseData) => void }) {
  const [mode, setMode] = useState<'scanner' | 'camera'>('scanner');
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [camera, setCamera] = useState<boolean | null>(null);
  const done = useRef(false);
  const lastBad = useRef('');
  const scanRef = useRef(onScan);
  scanRef.current = onScan;

  const take = (text: string, from: 'scanner' | 'camera') => {
    if (done.current) return;
    const d = parseLicense(text);
    if (d) {
      done.current = true;
      scanRef.current(d);
      return;
    }
    if (from === 'camera') {
      // The camera sees the same wrong barcode many times a second: say it once.
      if (text !== lastBad.current) setError('That barcode isn’t a license. Point the camera at the big barcode on the back.');
      lastBad.current = text;
    } else {
      setError(text.trim().length > 6 ? 'That wasn’t a license barcode. Scan the big barcode on the back of the license.' : null);
    }
  };

  useEffect(() => {
    cameraReadsLicenses().then(setCamera);
  }, []);

  // The USB scanner: every key while this is open is part of the barcode.
  useEffect(() => {
    if (mode !== 'scanner') return;
    let buf = '';
    let timer: ReturnType<typeof setTimeout> | undefined;
    const read = () => {
      const text = buf;
      buf = '';
      setReading(false);
      take(text, 'scanner');
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.isComposing) return;
      // Tab, Enter and Space with nothing scanned yet are someone using the keyboard.
      if (!buf && (e.key === 'Tab' || e.key === 'Enter' || e.key === ' ')) return;
      const ch = scannerChar(e);
      if (ch === null) {
        if (buf) {
          e.preventDefault();
          e.stopPropagation();
        }
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      buf += ch;
      setReading(true);
      setError(null);
      clearTimeout(timer);
      timer = setTimeout(read, 300);
    };
    const onPaste = (e: ClipboardEvent) => {
      const t = e.clipboardData?.getData('text') ?? '';
      if (!t) return;
      e.preventDefault();
      buf += t;
      clearTimeout(timer);
      timer = setTimeout(read, 30);
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('paste', onPaste, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('paste', onPaste, true);
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  return (
    <Sheet title="Scan their ID" onClose={onClose}>
      {mode === 'scanner' ? (
        <div className="stack">
          <div className={`scan-zone ${reading ? 'reading' : ''}`} role="status" aria-live="polite">
            <CardBack />
            <div className="strong">Scan the barcode on the back of the license</div>
            <div className="small muted">{reading ? 'Reading…' : 'Waiting for the scanner…'}</div>
          </div>
          {error ? <div className="error-text" role="alert">{error}</div> : null}
          <div className="row-wrap">
            <Button icon="scan" onClick={() => { setError(null); setMode('camera'); }}>Use the camera</Button>
          </div>
          <p className="small muted" style={{ margin: 0 }}>Works with any 2D barcode scanner that reads licenses (PDF417), like a Zebra DS2208 or Honeywell 1470g. It fills in the name, ID number, date of birth and address. Nothing is kept until you save.</p>
        </div>
      ) : (
        <div className="stack">
          {camera === false ? (
            <div className="banner warn"><Icon.alert /><div>This browser can’t read license barcodes with the camera. It works in Chrome on Android tablets and phones, and on Macs. Use the USB scanner here.</div></div>
          ) : camera ? (
            <>
              <CameraReader onText={(t) => take(t, 'camera')} onProblem={setError} />
              <p className="small muted" style={{ margin: 0, textAlign: 'center' }}>Hold the back of the license flat, with the big barcode inside the box, in good light.</p>
            </>
          ) : <p className="small muted">Starting the camera…</p>}
          {error ? <div className="error-text" role="alert">{error}</div> : null}
          <div className="row-wrap">
            <Button icon="idCard" onClick={() => { setError(null); setMode('scanner'); }}>Use the USB scanner</Button>
          </div>
        </div>
      )}
    </Sheet>
  );
}

/** What was read from the ID, with a warning when it's expired or the person is under 18. */
export function LicenseSummary({ d, adult = true }: { d: LicenseData; adult?: boolean }) {
  const today = todayIso();
  const age = d.dob ? ageOn(d.dob, today) : null;
  const expired = !!d.expires && isExpired(d.expires, today);
  const minor = adult && age !== null && age < 18;
  return (
    <div className={`id-pill ${expired || minor ? 'bad' : ''}`} aria-label="Scanned ID">
      <Icon.idCard />
      <div className="grow">
        <div className="strong">{d.fullName}</div>
        <div className="small muted">
          {[`${d.state ? `${d.state} ` : ''}${d.kind === 'ID' ? 'ID card' : 'driver license'} ···${d.number.slice(-4)}`,
            d.dob ? `born ${usDate(d.dob)}${age !== null ? ` (${age})` : ''}` : null,
            d.expires ? `${expired ? 'expired' : 'expires'} ${usDate(d.expires)}` : null].filter(Boolean).join(' · ')}
        </div>
      </div>
      {expired ? <Badge tone="red">Expired</Badge> : minor ? <Badge tone="red">Under 18</Badge> : <Badge tone="green">Read from the ID</Badge>}
    </div>
  );
}
